import * as React from "react";
import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Switch, Tooltip, useColorScheme } from "@mui/material";
import { trackEvent } from "../utils/gtag";
import { ACTION, EVENT } from "../utils/analytics-events";

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff",
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#aab4be",
        ...theme.applyStyles("dark", {
          backgroundColor: "#8796A5",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: "#001e3c",
    width: 32,
    height: 32,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        "#fff",
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
    ...theme.applyStyles("dark", {
      backgroundColor: "#003892",
    }),
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: "#aab4be",
    borderRadius: 20 / 2,
    ...theme.applyStyles("dark", {
      backgroundColor: "#8796A5",
    }),
  },
}));

/**
 * Detect if browser is in incognito/private mode
 * Works on Chrome iOS, Firefox, Safari
 */
const detectIncognitoMode = async (): Promise<boolean> => {
  try {
    const req = window.indexedDB?.open?.("test");
    return new Promise((resolve) => {
      if (req) {
        req.onsuccess = () => resolve(false);
        req.onerror = () => resolve(true);

        // Timeout fallback for slower devices
        setTimeout(() => resolve(false), 100);
      } else {
        resolve(false);
      }
    });
  } catch {
    return false;
  }
};

/**
 * Apply CodeMirror hotfix CSS for Chrome incognito dark mode bug
 */
const applyCodeMirrorHotfix = (): void => {
  const styleId = "codemirror-dark-mode-hotfix";

  // Prevent duplicate application
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    /* CodeMirror Dark Mode Chrome Incognito Hotfix */

    /* Force color-scheme for incognito CSS isolation */
    .cm-editor,
    .cm-editor * {
      color-scheme: dark !important;
      forced-color-adjust: none !important;
    }

    /* Ensure text is visible (not black on black) */
    .cm-editor .cm-line {
      color: #d4d4d4 !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    /* Disable virtual rendering containment */
    .cm-editor .cm-content {
      contain: none !important;
    }

    /* Mobile rendering optimizations */
    .cm-editor {
      -webkit-font-smoothing: antialiased;
      -webkit-text-size-adjust: 100%;
    }
  `;

  document.head.appendChild(style);

  // Apply inline style as backup for aggressive incognito isolation
  document.documentElement.style.colorScheme = "dark";
  document.body.style.colorScheme = "dark";
};

/**
 * Remove CodeMirror hotfix CSS and inline styles
 */
const removeCodeMirrorHotfix = (): void => {
  const style = document.getElementById("codemirror-dark-mode-hotfix");
  if (style) {
    style.remove();
  }

  // Clean up inline color-scheme overrides
  document.documentElement.style.colorScheme = "";
  document.body.style.colorScheme = "";
};

export const DarkMode = () => {
  const { mode, setMode } = useColorScheme();
  const [isIncognito, setIsIncognito] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Step 1: Detect incognito mode and mobile on mount
  useEffect(() => {
    (async () => {
      const incognito = await detectIncognitoMode();
      setIsIncognito(incognito);

      const mobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
      setIsMobile(mobile);

      // Auto-force dark mode in incognito on mobile (where bug occurs)
      if (incognito && mobile) {
        setMode("dark");
      }
    })();
  }, [setMode]);

  // Step 2: Apply/remove hotfix based on theme mode
  useEffect(() => {
    if (!mode) return;

    // Resolve actual theme mode
    const resolvedMode = (() => {
      if (mode === "system") {
        try {
          return window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light";
        } catch {
          return "light";
        }
      }
      return mode;
    })();

    // Update data attribute for existing theme logic
    document.body.dataset.agThemeMode = resolvedMode as string;

    // Apply or remove CodeMirror hotfix
    if (resolvedMode === "dark") {
      applyCodeMirrorHotfix();
    } else {
      removeCodeMirrorHotfix();
    }
  }, [mode]);

  // Step 3: Cleanup on component unmount
  useEffect(() => {
    return () => {
      removeCodeMirrorHotfix();
    };
  }, []);

  if (!mode) {
    return null;
  }

  // Hide theme switch in incognito mobile (dark mode is forced)
  if (isIncognito && isMobile) {
    return null;
  }

  return (
    <Tooltip
      title={mode === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
    >
      <MaterialUISwitch
        checked={mode === "dark"}
        data-testid="switchToDarkMode"
        slotProps={{ input: { "aria-label": "Switch dark mode" } }}
        sx={{ m: 1 }}
        onChange={(value) => {
          const next = value.target.checked ? "dark" : "light";
          trackEvent(EVENT.SETTINGS, {
            action: ACTION.DARK_MODE_TOGGLE,
            value: next,
          });
          setMode(next);
        }}
      />
    </Tooltip>
  );
};
